import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { paymentAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

let stripePromise = null;

function CheckoutForm() {
  const stripe = useStripe();
  const elements = useElements();
  const { setHasPaid } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!acceptedTerms) {
      setError('You must accept the Terms and Conditions to proceed with payment');
      toast.error('Please accept the Terms and Conditions');
      return;
    }

    setLoading(true);

    if (!stripe || !elements) {
      toast.error('Payment system is loading, please wait...');
      setLoading(false);
      return;
    }

    try {
      // Create payment intent
      const response = await paymentAPI.createPaymentIntent();
      const { clientSecret } = response.data;

      // Confirm payment
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              address: {
                country: 'CA',
              },
            },
          },
        }
      );

      if (stripeError) {
        const errorMsg = stripeError.message;
        setError(errorMsg);
        toast.error(errorMsg);
      } else if (paymentIntent.status === 'succeeded') {
        // Confirm payment with backend (backup for webhook)
        try {
          await paymentAPI.confirmPayment(paymentIntent.id);
        } catch (confirmErr) {
          console.error('Payment confirmation call failed:', confirmErr);
          // Continue anyway - webhook should handle it
        }

        setSuccess(true);
        setHasPaid(true);
        toast.success('Payment successful! Welcome to Premium 🎉');

        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Payment failed. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center">
        <div className="text-6xl mb-4">✅</div>
        <h3 className="text-2xl font-bold text-green-600 mb-2">
          Payment Successful!
        </h3>
        <p className="text-gray-600">Redirecting to dashboard...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-gray-50 p-4 rounded-lg">
        <CardElement
          options={{
            hidePostalCode: true,
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>

      {/* Terms and Conditions Checkbox */}
      <div className="flex items-start">
        <input
          type="checkbox"
          id="acceptTermsPayment"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
          className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
        />
        <label htmlFor="acceptTermsPayment" className="ml-3 text-sm text-gray-700">
          I agree to the{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-red-600 hover:text-red-700 underline"
          >
            Terms and Conditions
          </a>
          {' '}including the no-refund policy. I understand this is a practice tool with no guarantees.
        </label>
      </div>

      <button
        type="submit"
        disabled={!stripe || loading || !acceptedTerms}
        className="btn btn-primary w-full py-4 text-lg"
      >
        {loading ? 'Processing...' : 'Pay $14.99 CAD'}
      </button>

      <p className="text-center text-sm text-gray-500">
        Test card: 4242 4242 4242 4242
      </p>
    </form>
  );
}

export default function Payment() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await paymentAPI.getConfig();
      setConfig(response.data);
      stripePromise = loadStripe(response.data.publishableKey);
    } catch (error) {
      console.error('Failed to load payment config:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Loading payment...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-lg w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Upgrade to Premium</h1>
          <p className="text-gray-600">Get lifetime access to all features</p>
        </div>

        <div className="bg-gradient-to-r from-primary to-secondary text-white rounded-xl p-6 mb-8">
          <div className="text-center">
            <p className="text-4xl font-bold mb-2">$14.99 CAD</p>
            <p className="text-lg opacity-90">One-time payment • Lifetime access</p>
          </div>

          <div className="mt-6 space-y-3">
            <div className="text-center mb-4">
              <p className="font-semibold text-lg">Premium Features:</p>
            </div>
            <div className="flex items-center">
              <span className="mr-3 text-xl">✓</span>
              <span>Unlimited quiz attempts</span>
            </div>
            <div className="flex items-center">
              <span className="mr-3 text-xl">✓</span>
              <span>Detailed progress tracking</span>
            </div>
            <div className="flex items-center">
              <span className="mr-3 text-xl">✓</span>
              <span>Wrong answer review & analytics</span>
            </div>
            <div className="flex items-center">
              <span className="mr-3 text-xl">✓</span>
              <span>All exam modes (Timed, Practice, Exam)</span>
            </div>
            <div className="flex items-center">
              <span className="mr-3 text-xl">✓</span>
              <span>Achievement badges & streaks</span>
            </div>
            <div className="flex items-center">
              <span className="mr-3 text-xl">✓</span>
              <span>Priority support</span>
            </div>
          </div>
        </div>

        {stripePromise && (
          <Elements stripe={stripePromise}>
            <CheckoutForm />
          </Elements>
        )}

        <button
          onClick={() => navigate('/dashboard')}
          className="btn btn-secondary w-full mt-4"
        >
          Maybe Later
        </button>
      </div>
    </div>
  );
}
